import {
  ELEMENT_TYPES,
  VOID_TYPES,
  MARKDOWN_ELEMENTS,
  MARKDOWN_INLINES_CHECK,
  NEW_CHILD_ELEMENT_MENU_ITEMS,
  TOOLBAR_EXCLUDES,
  LEAF_TYPES
} from 'cg/components/documents/editor/slate/types'
import { Node, Transforms, Editor, Range, Path, Text } from 'vendor/slate'
import { HistoryEditor } from 'vendor/slate-history'
import addElement from 'cg/components/documents/editor/slate/addElement'
import { insertLink } from 'cg/components/documents/editor/slate/plugins/link'
import { v4 as uuidv4 } from 'uuid'
import deepUnfreeze from 'deep-unfreeze'
import { jsx } from 'vendor/slate-hyperscript'
import { insertCodeTabs } from 'cg/components/documents/editor/slate/plugins/code'
import { sanitizePastedFragment } from 'cg/components/documents/editor/slate/pasteSanitize'

const MARKDOWN_SPACER = ' '

// Start a fresh undo step, then record every op in `fn` through Slate history.
const runAsSingleHistoryBatch = (editor, fn) => {
  if (!HistoryEditor.isHistoryEditor(editor)) {
    Editor.withoutNormalizing(editor, fn)
    return
  }

  HistoryEditor.withNewBatch(editor, () => {
    Editor.withoutNormalizing(editor, fn)
  })
}

const MARKDOWN_UNSUPPORTED = [
  'code_tabs',
  'code_tabs_content_item',
  'code_tabs_content_item_line',
  'code_tabs_header',
  'code_tabs_header_item'
]

const HTML_UNSUPPORTED = [
  'code_tabs',
  'code_tabs_content_item',
  'code_tabs_content_item_line',
  'code_tabs_header',
  'code_tabs_header_item'
]

const MARKDOWN_NEW_NODE_TYPE = [
  'quote',
  'unordered_list',
  'ordered_list',
  'list_item',
  'code_block',
  'code_tabs',
  'horizontal_rule',
  'image',
  'header_one',
  'header_two',
  'header_three',
  'header_four',
  'header_five',
  'header_six',
  'hint',
  'hint_content'
]

const PASTE_BLOCK_INSERT_TYPES = [
  ...MARKDOWN_NEW_NODE_TYPE,
  ELEMENT_TYPES.unordered_list,
  ELEMENT_TYPES.ordered_list,
  ELEMENT_TYPES.check_list
]

const looksLikeMarkdownPaste = text => {
  if (!text || text.length <= 1) return false

  return (
    /```/.test(text) ||
    /^#{1,6}\s/m.test(text) ||
    /^>\s/m.test(text) ||
    /^\s*[-*+]\s/m.test(text) ||
    /^\s*\d+\.\s/m.test(text) ||
    /^\s*[-*]{3,}\s*$/m.test(text) ||
    /!\[[^\]]*\]\([^)]+\)/.test(text) ||
    /\[[^\]]+\]\([^)]+\)/.test(text) ||
    /<[A-Z][A-Za-z0-9]*/.test(text) ||
    /<(img|br|hr|a|code|pre|table)\b/i.test(text)
  )
}

const prefersPlainMarkdownPaste = (plain, html) => {
  if (!plain || !html || plain.length <= 1) return false
  return looksLikeMarkdownPaste(plain)
}

const canMarkdownPasteHere = parentNode =>
  (parentNode && parentNode.parent && !parentNode.parent.type) ||
  (parentNode &&
    parentNode.parent.type &&
    !MARKDOWN_UNSUPPORTED.includes(parentNode.parent.type))

const pasteNeedsBlockInsert = sanitized =>
  sanitized.some(n => n && PASTE_BLOCK_INSERT_TYPES.includes(n.type))

const insertPastedNodes = (editor, nodes) => {
  const sanitized = sanitizePastedFragment(nodes)

  runAsSingleHistoryBatch(editor, () => {
    if (editor.selection && Range.isExpanded(editor.selection)) {
      Transforms.delete(editor)
    }

    if (pasteNeedsBlockInsert(sanitized)) {
      Transforms.insertNodes(editor, sanitized)
    } else {
      editor.insertFragment(sanitized)
    }
  })
}

const insertRawMarkdown = (editor, markdown) => {
  const node = {
    type: ELEMENT_TYPES.raw_markdown,
    uuid: uuidv4(),
    parent: { type: null },
    children: [{ uuid: uuidv4(), text: markdown, parent: { type: ELEMENT_TYPES.raw_markdown } }]
  }

  runAsSingleHistoryBatch(editor, () => {
    if (editor.selection && Range.isExpanded(editor.selection)) {
      Transforms.delete(editor)
    }
    Transforms.insertNodes(editor, node)
  })
}

const insertMarkdownFromPlain = (editor, text, parentNode, fallbackInsertText) => {
  if (!looksLikeMarkdownPaste(text) || !canMarkdownPasteHere(parentNode)) {
    fallbackInsertText(text)
    return
  }

  insertRawMarkdown(editor, text)
}

const SPLIT_LINE_NODES = ['code_tabs', 'code_tabs_content_item', 'code_tabs_content_item_line']

const ELEMENT_TAGS = {
  A: el => ({ type: ELEMENT_TYPES.link, url: el.getAttribute('href') }),
  BLOCKQUOTE: () => ({ type: 'quote' }),
  H1: () => ({ type: ELEMENT_TYPES.header_one }),
  H2: () => ({ type: ELEMENT_TYPES.header_two }),
  H3: () => ({ type: ELEMENT_TYPES.header_three }),
  H4: () => ({ type: ELEMENT_TYPES.header_three }),
  H5: () => ({ type: ELEMENT_TYPES.header_three }),
  H6: () => ({ type: ELEMENT_TYPES.header_three }),
  IMG: el => ({ type: ELEMENT_TYPES.image, url: el.getAttribute('src') }),
  LI: () => ({ type: ELEMENT_TYPES.list_item }),
  OL: () => ({ type: ELEMENT_TYPES.ordered_list }),
  P: () => ({ type: ELEMENT_TYPES.default }),
  PRE: () => ({ type: ELEMENT_TYPES.code_no_tabs }),
  UL: () => ({ type: ELEMENT_TYPES.unordered_list })
}

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underlined: true })
}

const updateUUidInNode = (id, obj) => {
  const traverseAndUpdate = currentObj => {
    if (Array.isArray(currentObj)) {
      currentObj.forEach(item => traverseAndUpdate(item))
    } else if (currentObj && typeof currentObj === 'object') {
      Object.keys(currentObj).forEach(key => {
        if (key === id) {
          currentObj[key] = uuidv4() // Update with new UUID
        } else if (typeof currentObj[key] === 'object') {
          traverseAndUpdate(currentObj[key]) // Recursively update nested objects/arrays
        }
      })
    }
  }

  traverseAndUpdate(obj)
}

const UUID_V4_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

// Produces a fresh, unique id while preserving the original id's shape. Ids in
// the editor look like `code-<uuid>-1`, `table-thead-<uuid>`, `tr-<uuid>`, etc.
// Swapping the embedded uuid keeps the prefix/suffix (and any tab/defaultActiveTab
// relationships) intact while still guaranteeing uniqueness across a paste.
const regenerateNodeId = value => {
  if (typeof value !== 'string' || !value) {
    return value
  }
  if (UUID_V4_PATTERN.test(value)) {
    return value.replace(UUID_V4_PATTERN, uuidv4())
  }
  if (/\b(?:code|api-code|api|api-tab|tab)-/.test(value)) {
    return value.replace(/(\b(?:code|api-code|api|api-tab|tab)-).*/, `$1${uuidv4()}`)
  }
  return `${value}-${uuidv4()}`
}

const updateTabIdInNode = (obj, targetKeys) => {
  const firstOccurrences = new Map()
  const replaceValues = (item, newValueMap) => {
    if (Array.isArray(item)) {
      item.forEach(child => replaceValues(child, newValueMap))
    } else if (item && typeof item === 'object') {
      Object.keys(item).forEach(key => {
        if (targetKeys.includes(key) && newValueMap.has(item[key])) {
          item[key] = newValueMap.get(item[key])
        } else if (item[key] && typeof item[key] === 'object') {
          replaceValues(item[key], newValueMap)
        }
      })
    }
  }

  // Collect first occurrences
  const collectFirstOccurrences = item => {
    if (Array.isArray(item)) {
      item.forEach(collectFirstOccurrences)
    } else if (item && typeof item === 'object') {
      Object.keys(item).forEach(key => {
        if (targetKeys.includes(key) && typeof item[key] === 'string' && !firstOccurrences.has(item[key])) {
          const newId = regenerateNodeId(item[key])
          firstOccurrences.set(item[key], newId)
          item[key] = newId
        }
        collectFirstOccurrences(item[key])
      })
    }
  }

  collectFirstOccurrences(obj)
  replaceValues(obj, firstOccurrences)
}

const deserialize = el => {
  if (el.nodeType === 3) {
    return el.textContent
  }
  if (el.nodeType !== 1) {
    return null
  }
  if (el.nodeName === 'BR') {
    return '\n'
  }

  const { nodeName } = el
  let parent = el

  if (nodeName === 'PRE' && el.childNodes[0] && el.childNodes[0].nodeName === 'CODE') {
    parent = el.childNodes[0]
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat()

  if (children.length === 0) {
    children = [{ text: '' }]
  }

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children)
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el)
    return jsx('element', attrs, children)
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el)
    return children.map(child => jsx('text', attrs, child))
  }

  return children
}

const withDefaults = editor => {
  const { isVoid, insertBreak, insertText, normalizeNode, insertData } = editor

  editor.isVoid = element => (VOID_TYPES.includes(element.type) ? true : isVoid(element))

  editor.normalizeNode = entry => {
    const [node, path] = entry

    // Normalize default paragraph
    if (node.type === ELEMENT_TYPES.default) {
      if (!('children' in node) || node.children.length === 0) {
        Editor.withoutNormalizing(editor, () => {
          Transforms.removeNodes(editor, { at: path })
          Transforms.insertNodes(
            editor,
            {
              type: ELEMENT_TYPES.default,
              parent: { type: null },
              children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
            },
            { at: path }
          )
        })
        return
      }
    }

    // Normalize code no tabs (legacy PRE paste) into code_tabs
    if (node.type === ELEMENT_TYPES.code_no_tabs) {
      Editor.withoutNormalizing(editor, () => {
        const [[text]] = Node.texts(node)
        Transforms.removeNodes(editor, { at: path })
        const firstTabId = insertCodeTabs(editor, path)

        const [match] = Editor.nodes(editor, {
          at: path,
          match: n => !Editor.isEditor(n) && n.type === ELEMENT_TYPES.code_tabs_content_item
        })

        if (match) {
          const [, contentPath] = match
          const texts = text?.text.split(/[\r\n]+/) || ['']
          const children = []

          const new_code_tabs_content_item = {
            type: ELEMENT_TYPES.code_tabs_content_item,
            tabId: firstTabId,
            parent: { type: ELEMENT_TYPES.code_tabs },
            language: 'plaintext',
            children: []
          }
          texts.forEach(txt => {
            children.push({
              type: ELEMENT_TYPES.code_tabs_content_item_line,
              parent: { type: ELEMENT_TYPES.code_tabs_content_item },
              children: [{ uuid: uuidv4(), text: txt, parent: { type: ELEMENT_TYPES.code_tabs_content_item_line } }]
            })
          })
          new_code_tabs_content_item.children = children
          Transforms.removeNodes(editor, { at: contentPath })
          Transforms.insertNodes(editor, new_code_tabs_content_item, { at: contentPath })
        }
      })
      return
    }

    normalizeNode(entry)
  }

  // Block breakout on enter
  editor.insertBreak = () => {
    const { selection } = editor

    if (selection) {
      const [rawMarkdownMatch] = Editor.nodes(editor, {
        at: selection,
        match: n => !Editor.isEditor(n) && n.type === ELEMENT_TYPES.raw_markdown
      })
      if (rawMarkdownMatch) {
        Transforms.insertText(editor, '\n')
        return
      }

      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && n.parent && n.parent.type === ELEMENT_TYPES.default
      })

      if (!match) {
        Transforms.insertNodes(editor, {
          type: ELEMENT_TYPES.default,
          parent: { type: null },
          children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
        })
        return
      }
    }

    insertBreak()
  }

  editor.insertText = text => {
    const { selection } = editor

    if (text.includes('application/x-slate-clipboard:')) {
      return
    }
    if (selection) {
      const parentPath = Path.parent(selection.anchor.path)
      const parentNode = Node.has(editor, parentPath) ? Node.get(editor, parentPath) : null
      if (parentNode?.type === ELEMENT_TYPES.raw_markdown) {
        insertText(text)
        return
      }
    }
    if (text === MARKDOWN_SPACER && selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const { path } = anchor

      const node = Node.get(editor, path)
      let texts = { text: '' }

      if (!node) return

      try {
        const [[text]] = Node.texts(node)
        texts = text
      } catch (err) {}

      const range = { anchor, focus: { path, offset: 0 } }

      // Add check before inserting that element is allowed to be inserted
      if (MARKDOWN_ELEMENTS[texts.text]) {
        const parentNode = Node.has(editor, Path.parent(path)) ? Node.get(editor, Path.parent(path)) : null

        if (
          (path[1] === 0 && parentNode && parentNode.parent && !parentNode.parent.type) ||
          (parentNode &&
            parentNode.parent.type &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[parentNode.parent.type] &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[parentNode.parent.type].valid_items.indexOf(MARKDOWN_ELEMENTS[texts.text]) !== -1)
        ) {
          Transforms.select(editor, range)
          Transforms.delete(editor)
          addElement(editor, node, MARKDOWN_ELEMENTS[texts.text], Path.parent(path))
          return
        }
      }
    }

    if (MARKDOWN_INLINES_CHECK.indexOf(text) !== -1 && selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const { path } = anchor
      const [start] = Range.edges(selection)

      const inverseLinkRegex = /.*?\(].*?\[/g
      const wordBefore = Editor.before(
        editor,
        { path: start.path, offset: start.offset + 1 },
        {
          unit: 'word',
          skipPattern: text === ')' ? inverseLinkRegex : null,
          skipChars: MARKDOWN_INLINES_CHECK.concat(['[', ']', ':', '/', '(', '.'])
        }
      )
      const before = wordBefore && Editor.before(editor, wordBefore)
      const beforeRange = before && Editor.range(editor, before, { path: start.path, offset: start.offset + 1 })
      const beforeText = beforeRange && Editor.string(editor, beforeRange) + text
      const isSamePath = beforeRange && beforeRange.anchor.path.join('_') === beforeRange.focus.path.join('_')
      const deleteRange = beforeRange && {
        anchor: {
          path: isSamePath ? beforeRange.anchor.path : beforeRange.focus.path,
          offset: isSamePath ? beforeRange.anchor.offset : 0
        },
        focus: { path: beforeRange.focus.path, offset: beforeRange.focus.offset }
      }
      const parentNode = Node.has(editor, Path.parent(path)) ? Node.get(editor, Path.parent(path)) : null

      const strongRegex = /(\*\*|__)(.*?)\1/g
      const emphasisRegex = /(\*|_)(.*?)\1/g
      const strikethroughRegex = /\~(.*?)\~/g
      const codeRegex = /`(.*?)`/g
      const linkRegex = /\[([^\[]+)\]\(([^\)]+)\)/g

      let matches = []

      switch (text) {
        case '*':
        case '_':
          if (
            beforeText &&
            ((parentNode && parentNode.parent && !parentNode.parent.type) ||
              (parentNode && parentNode.parent.type && !TOOLBAR_EXCLUDES.includes(parentNode.parent.type)))
          ) {
            // Bold Mark
            matches = [...beforeText.matchAll(strongRegex)]

            if (matches[0] && matches[0].length && matches[0][2] && matches[0][2].length) {
              Transforms.delete(editor, { at: deleteRange })
              Editor.insertText(editor, matches[0][2])
              Transforms.setNodes(
                editor,
                { [LEAF_TYPES.bold]: true },
                {
                  match: Text.isText,
                  at: {
                    anchor: {
                      path: deleteRange.anchor.path,
                      offset: deleteRange.anchor.offset
                    },
                    focus: {
                      path: deleteRange.focus.path,
                      offset: deleteRange.anchor.offset + matches[0][2].length
                    }
                  },
                  split: true
                }
              )
              Editor.removeMark(editor, LEAF_TYPES.bold)
              return
            }

            // Emphasis mark
            matches = [...beforeText.matchAll(emphasisRegex)]
            if (matches[0] && matches[0].length && matches[0][2] && matches[0][2].length) {
              Transforms.delete(editor, { at: deleteRange })
              Editor.insertText(editor, matches[0][2])
              Transforms.setNodes(
                editor,
                { [LEAF_TYPES.italic]: true },
                {
                  match: Text.isText,
                  at: {
                    anchor: {
                      path: deleteRange.anchor.path,
                      offset: deleteRange.anchor.offset
                    },
                    focus: {
                      path: deleteRange.focus.path,
                      offset: deleteRange.anchor.offset + matches[0][2].length
                    }
                  },
                  split: true
                }
              )
              Editor.removeMark(editor, LEAF_TYPES.italic)
            }
          }
          break
        case '~':
          if (
            beforeText &&
            ((parentNode && parentNode.parent && !parentNode.parent.type) ||
              (parentNode && parentNode.parent.type && !TOOLBAR_EXCLUDES.includes(parentNode.parent.type)))
          ) {
            // Strikethrough
            matches = [...beforeText.matchAll(strikethroughRegex)]

            if (matches[0] && matches[0].length && matches[0][1] && matches[0][1].length) {
              Transforms.delete(editor, { at: deleteRange })
              Editor.insertText(editor, matches[0][1])
              Transforms.setNodes(
                editor,
                { [LEAF_TYPES.strikethrough]: true },
                {
                  match: Text.isText,
                  at: {
                    anchor: {
                      path: deleteRange.anchor.path,
                      offset: deleteRange.anchor.offset
                    },
                    focus: {
                      path: deleteRange.focus.path,
                      offset: deleteRange.anchor.offset + matches[0][1].length
                    }
                  },
                  split: true
                }
              )
              Editor.removeMark(editor, LEAF_TYPES.strikethrough)
            }
          }
          break
        case '`':
          if (
            beforeText &&
            ((parentNode && parentNode.parent && !parentNode.parent.type) ||
              (parentNode && parentNode.parent.type && !TOOLBAR_EXCLUDES.includes(parentNode.parent.type)))
          ) {
            // code
            matches = [...beforeText.matchAll(codeRegex)]

            if (matches[0] && matches[0].length && matches[0][1] && matches[0][1].length) {
              Transforms.delete(editor, { at: deleteRange })
              Editor.insertText(editor, matches[0][1])
              Transforms.setNodes(
                editor,
                { [LEAF_TYPES.code]: true },
                {
                  match: Text.isText,
                  at: {
                    anchor: {
                      path: deleteRange.anchor.path,
                      offset: deleteRange.anchor.offset
                    },
                    focus: {
                      path: deleteRange.focus.path,
                      offset: deleteRange.anchor.offset + matches[0][1].length
                    }
                  },
                  split: true
                }
              )
              Editor.removeMark(editor, LEAF_TYPES.code)
            }
          }
          break
        case ')':
          if (
            beforeText &&
            ((parentNode && parentNode.parent && !parentNode.parent.type) ||
              (parentNode && parentNode.parent.type && !TOOLBAR_EXCLUDES.includes(parentNode.parent.type)))
          ) {
            // Links
            matches = [...beforeText.matchAll(linkRegex)]
            if (
              matches[0] &&
              matches[0].length &&
              matches[0][1] &&
              matches[0][1].length &&
              matches[0][2] &&
              matches[0][2].length
            ) {
              Transforms.delete(editor, { at: deleteRange })
              insertLink(editor, matches[0][2], true, matches[0][1])
            }
          }
          break
        default:
          break
      }
    }

    if (text && text.length > 1) {
      const { anchor } = selection
      const { path } = anchor
      const parentNode = Node.has(editor, Path.parent(path)) ? Node.get(editor, Path.parent(path)) : null

      insertMarkdownFromPlain(editor, text, parentNode, chunk => insertText(chunk))
    } else {
      insertText(text)
    }
  }

  editor.insertData = data => {
    const { insertFragment } = editor
    const fragment = data.getData('application/x-slate-fragment')
    const html = data.getData('text/html')
    const { selection, marks } = editor

    if (!selection) {
      return
    }

    const { anchor } = selection
    const { path } = anchor
    const node = Node.has(editor, path) ? Node.get(editor, path) : null
    const parentNode = Node.has(editor, Path.parent(path)) ? Node.get(editor, Path.parent(path)) : null

    const [start, end] = Range.edges(selection)
    const isNewNode = selection && start.offset === 0 && end.offset === 0

    const plain = data.getData('text/plain')

    if (parentNode?.type === ELEMENT_TYPES.raw_markdown && plain) {
      Transforms.insertText(editor, plain)
      editor.marks = null
      return
    }

    if (
      !fragment &&
      plain &&
      html &&
      prefersPlainMarkdownPaste(plain, html) &&
      !HTML_UNSUPPORTED.includes(parentNode?.parent?.type)
    ) {
      insertMarkdownFromPlain(editor, plain, parentNode, chunk => {
        if (marks) {
          Transforms.insertNodes(editor, { text: chunk, ...marks })
        } else {
          editor.insertText(chunk)
        }
        editor.marks = null
      })
      return
    }

    if (!fragment && html && !HTML_UNSUPPORTED.includes(parentNode?.parent?.type)) {
      const parsed = new DOMParser().parseFromString(html, 'text/html')
      const deserialized = deserialize(parsed.body)
      insertPastedNodes(editor, deserialized)
      return
    }

    if (fragment) {
      const decoded = decodeURIComponent(window.atob(fragment))
      const parsed = JSON.parse(decoded)

      if (parsed && parsed.length) {
        runAsSingleHistoryBatch(editor, () => {
          const newParsed = []
          parsed.forEach(node => {
            const { type } = node

            if (type) {
              // Any typed block copied from another Slate doc (paragraph, list,
              // image, table, code_tabs, hint, mention, ...) is already a valid
              // editor node. Insert it whole with fresh uuids/ids so it never
              // collides with the source node, instead of stripping it to text.
              const newNode = deepUnfreeze(node)
              updateUUidInNode('uuid', newNode)
              updateTabIdInNode(newNode, ['id', 'defaultActiveTab', 'tabId'])

              if (isNewNode) {
                Transforms.insertNodes(editor, newNode)
              } else {
                newParsed.push(newNode)
              }
            } else {
              let texts = []
              try {
                texts = Array.from(Node.texts(node))
              } catch (err) {}
              if (texts && texts.length > 0) {
                texts.forEach(text => {
                  if (text[0] && 'text' in text[0]) {
                    const defaultNode = {
                      type: ELEMENT_TYPES.default,
                      parent: { type: null },
                      children: [{ uuid: uuidv4(), text: text[0].text, parent: { type: ELEMENT_TYPES.default } }]
                    }
                    if ('code' in text[0]) {
                      defaultNode.children[0].code = text[0].code
                    }
                    if ('mark' in text[0]) {
                      defaultNode.children[0].mark = text[0].mark
                    }
                    if (isNewNode) {
                      Transforms.insertNodes(editor, defaultNode)
                    } else {
                      newParsed.push(defaultNode)
                    }
                  }
                })
              }
            }
          })

          if (!isNewNode) {
            editor.insertFragment(newParsed)
          }
        })
        return
      }
      if (html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html')
        const fragment = deserialize(parsed.body)
        insertPastedNodes(editor, fragment)
        return
      }
    }

    const text = plain || data.getData('text/plain')

    if (text) {
      const slateClipboard = text.split('application/x-slate-clipboard:')[1] || null
      const { anchor } = selection
      const { path } = anchor
      const parentNode = Node.has(editor, Path.parent(path)) ? Node.get(editor, Path.parent(path)) : null

      if (slateClipboard) {
        const decoded = decodeURIComponent(window.atob(slateClipboard))
        const parsed = JSON.parse(decoded)

        if (
          !parentNode?.parent?.type ||
          (node?.type &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[node?.type] &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[node?.type].valid_items.indexOf(parsed?.type) !== -1) ||
          (parentNode?.type &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[parentNode?.type] &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[parentNode?.type].valid_items.indexOf(parsed?.type) !== -1) ||
          (parentNode?.type &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[parentNode?.parent?.type] &&
            NEW_CHILD_ELEMENT_MENU_ITEMS[parentNode?.parent?.type].valid_items.indexOf(parsed?.type) !== -1)
        ) {
          updateUUidInNode('uuid', parsed)
          updateTabIdInNode(parsed, ['id', 'defaultActiveTab', 'tabId'])
          Transforms.insertNodes(editor, parsed)
          return
        }
      }

      if (text.includes('application/x-slate-clipboard:')) {
        return
      }

      if (parentNode && parentNode.parent.type && SPLIT_LINE_NODES.includes(parentNode.parent.type)) {
        const lines = text.split(/\r\n|\r|\n/)
        let split = false

        for (const line of lines) {
          if (split) {
            Transforms.splitNodes(editor, { always: true })
          }

          if (marks) {
            const node = { line, ...marks }
            Transforms.insertNodes(editor, node)
          } else {
            Transforms.insertText(editor, line)
          }

          editor.marks = null
          split = true
        }
      } else if (selection) {
        if (marks) {
          const node = { text, ...marks }
          Transforms.insertNodes(editor, node)
        } else if (looksLikeMarkdownPaste(text)) {
          insertMarkdownFromPlain(editor, text, parentNode, chunk => insertText(chunk))
        } else {
          insertText(text)
        }

        editor.marks = null
      }
    }
  }

  return editor
}

export const trimTabsAndNewLines = text => {
  if ((text && !text.length) || !text) return
  return text.replace(/ {2}|\r\n|\n|\r/gm, '')
}

export { withDefaults }
